import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components/native';

import { H5 } from '../../components/Typography';
import Button from '../../components/Button';
import Spacer from '../../components/Spacer';
import { StackNavigationProp } from '@react-navigation/stack';
import {
  RouteProp,
  StackActions,
  useFocusEffect,
  useNavigation,
  useRoute
} from '@react-navigation/native';
import { QualificationParamList } from '../../web-navigation/QualificationNavigator';
import { FormElementMeta } from '@people-science/forms-core';
import { camelCase } from 'lodash';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import DynamicForm from '../../dynamic-forms/DynamicForm';
import DynamicFormProvider from '../../dynamic-forms/DynamicFormProvider';
import { useGetFormTemplate } from '../../dynamic-forms/hooks/useGetFormTemplate';
import { useRunQuery } from '../../dynamic-forms/hooks/useRunQuery';
import { useUserContext } from '../../context/UserContext';
import QualificationFormDataSource from './QualificationFormDataSource';

const StyledFormNotFoundContainerView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const QualificationFormScreen: React.FC = () => {
  const { user } = useUserContext();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route =
    useRoute<RouteProp<QualificationParamList, 'QualificationForm'>>();
  const { formTemplateId, studyParticipantId, path, newEntry, fromJoinScreen } =
    route.params || {};
  const entityDataIdRef = useRef(route.params?.entityDataId);
  entityDataIdRef.current = route.params?.entityDataId;
  const history: string[] = route.params?.history
    ? JSON.parse(route.params.history)
    : [];
  // fields to focus on and complete
  let dataSourceFields: string[] = useMemo(
    () =>
      route.params?.dataSourceFields
        ? JSON.parse(route.params.dataSourceFields)
        : [],
    [route.params?.dataSourceFields]
  );
  const { formTemplate, loadingFormTemplate, getFormTemplate } =
    useGetFormTemplate(formTemplateId || '');

  const statePath = useMemo(() => {
    const stateNodes = formTemplate?.config?.states;
    if (dataSourceFields.length === 0 || !stateNodes) return path;
    for (const stateNode of stateNodes) {
      if (!stateNode.meta?.elements) continue;
      if (
        stateNode.meta.elements.some(e =>
          dataSourceFields.includes(e.dataSource || '')
        )
      ) {
        return `/${formTemplate.config?.name}/${stateNode.name}`;
      }
    }
    return path;
  }, [dataSourceFields, formTemplate?.config, path]);
  // Load the participant event form data on mount as well as refresh the data
  // when the screen comes into focus from the route navigation via
  // useFocusEffect to prevent display of previous state.
  useFocusEffect(
    useCallback(() => {
      if (formTemplateId) getFormTemplate();
    }, [getFormTemplate, formTemplateId])
  );

  // run query to get entity data
  const { queryData, isRunningQuery, runQueryGetOne } = useRunQuery();
  useFocusEffect(
    useCallback(() => {
      if (entityDataIdRef.current && user?.id && formTemplate?.entity) {
        const entitySchema = formTemplate?.entity;
        const alias = camelCase(entitySchema.name);
        runQueryGetOne({
          queryType: 'select',
          selects: [],
          mainAlias: {
            type: 'from',
            name: alias,
            tablePath: entitySchema.tableName
          },
          wheres: [
            {
              type: 'simple',
              condition: {
                operator: 'equal',
                parameters: [`${alias}.id`, entityDataIdRef.current.toString()]
              }
            }
          ],
          orderBys: [{ propertyPath: `${alias}.created`, order: 'DESC' }]
        });
      }
    }, [user?.id, formTemplate?.entity, runQueryGetOne])
  );

  // load existing data if any
  const [formValues, setFormValues] = useState(queryData);
  useEffect(() => {
    if (queryData) {
      setFormValues(queryData);
    } else {
      setFormValues({});
    }
  }, [queryData]);

  // display spinner while loading elements to prevent flickering
  const [loadingElements, setLoadingElements] = useState(false);

  const readyToDisplay = useMemo(
    () => (entityDataIdRef.current ? Boolean(queryData && formValues) : true),
    [formValues, queryData]
  );
  return !readyToDisplay || isRunningQuery || loadingFormTemplate ? (
    <ActivityIndicatorScreen />
  ) : !formTemplateId || !formTemplate ? (
    <StyledFormNotFoundContainerView>
      <H5 color='light'>Qualification Form Not Found</H5>
      <Spacer size={3} />
      <Button
        outlined
        size='small'
        onPress={() => {
          navigation.goBack();
        }}
      >
        Back
      </Button>
    </StyledFormNotFoundContainerView>
  ) : (
    <DynamicFormProvider
      entitySchema={formTemplate?.entity}
      formStateNode={formTemplate?.config}
      onLoadProgress={loadingElementQueue => {
        setLoadingElements(loadingElementQueue.length > 0);
      }}
    >
      <QualificationFormDataSource
        studyParticipantId={studyParticipantId}
        formTemplate={formTemplate}
        formData={formValues}
        onCreated={entity => {
          entityDataIdRef.current = entity.id;
          const params = { entityDataId: entity.id } as any;
          if (entity.studyParticipantId)
            params.studyParticipantId = entity.studyParticipantId;
          navigation.setParams(params);
        }}
      >
        <DynamicForm
          values={formValues}
          statePath={statePath}
          onBack={() => {
            navigation.pop();
          }}
          onStatePathChange={(
            path,
            stateNode,
            sectionIndex,
            sectionCount,
            stateMachine
          ) => {
            if (sectionIndex < sectionCount) {
              if (statePath && dataSourceFields.length > 0) {
                const previousStateNode = stateMachine.getStateNode(statePath);
                dataSourceFields = dataSourceFields.filter(d => {
                  if (
                    previousStateNode?.meta?.elements?.some(
                      (e: FormElementMeta<any, any>) => e.dataSource === d
                    )
                  ) {
                    return false; // remove data source field if in stateNode
                  }
                  return true;
                });
                if (dataSourceFields.length === 0) {
                  // navigate to previous screen after completing all
                  // dataSourceFields related questions
                  const navigationState = navigation.getState();
                  let popCount = 0;
                  for (let i = navigationState.routes.length - 1; i >= 0; i--) {
                    if (
                      navigationState.routes[i].name !== 'QualificationForm'
                    ) {
                      navigation.pop(popCount);
                      return;
                    }
                    popCount++;
                  }
                }
              }
              const newSectionIndex = sectionIndex + 1;
              history.push(path);
              navigation.push('QualificationForm', {
                path,
                entityDataId: entityDataIdRef.current,
                sectionIndex: newSectionIndex,
                formTemplateId,
                studyParticipantId:
                  studyParticipantId || queryData?.studyParticipantId,
                history: JSON.stringify(history),
                fromJoinScreen: fromJoinScreen
              });
            } else if (sectionIndex >= sectionCount) {
              // NOTE: technically this would never occur (you can't configure
              // a form transition past the last section)
              navigation.dispatch(
                StackActions.replace('QualificationNavigator')
              );
            }
          }}
        />
      </QualificationFormDataSource>
      {loadingElements && (
        <ActivityIndicatorScreen modal backgroundColor='#fff' />
      )}
    </DynamicFormProvider>
  );
};

export default QualificationFormScreen;
